/**
 * seededRandom — Deterministic, reproducible random number generation.
 *
 * Implements xorshift32, a small non-cryptographic PRNG that fits in a
 * single 32-bit integer of state. Demos that visualize stochastic
 * behavior rely on a seeded generator so each frame, each replay, and
 * each screenshot is byte-identical for the same seed.
 *
 * Not suitable for cryptographic use — `crypto.getRandomValues` should
 * be used wherever unpredictability is required.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

export interface SeededRandom {
  /** Next pseudo-random number in [0, 1). */
  readonly next: () => number;
  /** Next pseudo-random integer in [low, high). */
  readonly nextInt: (lowInclusive: number, highExclusive: number) => number;
  /** Next pseudo-random float in [low, high). */
  readonly nextFloat: (lowInclusive: number, highExclusive: number) => number;
  /** Pick a uniformly random element from the given array. */
  readonly pick: <T>(items: readonly T[]) => T;
  /** Yield `true` with the given probability in [0, 1]. */
  readonly bernoulli: (probability: number) => boolean;
  /** Current internal state — exposed so callers can persist & resume. */
  readonly state: () => number;
}

/**
 * Create a seeded xorshift32 random generator.
 *
 * @param seed - 32-bit integer seed. Zero is illegal under xorshift; it
 *   is silently replaced with `0x9E3779B9` (the golden-ratio constant
 *   used by SplitMix) so callers can pass any number including `0`.
 */
export function createSeededRandom(seed: number): SeededRandom {
  let state = (seed | 0) === 0 ? 0x9e3779b9 : seed | 0;

  const next = (): number => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    return (state >>> 0) / 0x100000000;
  };

  const nextFloat = (lowInclusive: number, highExclusive: number): number =>
    lowInclusive + next() * (highExclusive - lowInclusive);

  const nextInt = (lowInclusive: number, highExclusive: number): number =>
    Math.floor(nextFloat(lowInclusive, highExclusive));

  const pick = <T>(items: readonly T[]): T => {
    if (items.length === 0) {
      throw new Error("createSeededRandom.pick: array is empty");
    }
    const picked = items[nextInt(0, items.length)];
    if (picked === undefined) {
      throw new Error("createSeededRandom.pick: selected index was out of range");
    }
    return picked;
  };

  const bernoulli = (probability: number): boolean => next() < probability;

  return {
    next,
    nextInt,
    nextFloat,
    pick,
    bernoulli,
    state: (): number => state >>> 0,
  };
}
